import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import "../../styles/field.scss";

interface Option {
  label: string;
  value: string | number;
}

interface SelectProps {
  id?: string;
  name?: string;
  label?: React.ReactNode;
  error?: boolean;
  helperText?: React.ReactNode;
  options: Array<string | Option>;
  value?: string | number;
  onChange: (value: any) => void;
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
  /** Goes on the outer wrapper. */
  wrapperClassName?: string;
}

const toOption = (option: string | Option): Option =>
  typeof option === "string" ? { label: option, value: option } : option;

const MENU_MAX_HEIGHT = 216;
/** Breathing room between the control and the list, and at the viewport edge. */
const MENU_GAP = 6;
const VIEWPORT_MARGIN = 12;

const Select: React.FC<SelectProps> = ({
  id,
  name,
  label,
  error = false,
  helperText,
  options,
  value,
  onChange,
  disabled = false,
  placeholder = "Select an option",
  required = false,
  wrapperClassName = "",
}) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const rootRef = useRef<HTMLDivElement | null>(null);
  const controlRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const generatedId = useId();
  const fieldId = id || name || generatedId;
  const helperId = `${fieldId}-helper`;

  const items = (options || []).map(toOption);
  const selected = items.find(
    (option) => String(option.value) === String(value),
  );

  useEffect(() => {
    if (!open) return;

    const onDocumentPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      // The list is portalled out, so it is not inside rootRef.
      if (rootRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onDocumentPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocumentPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // The list is portalled to the body and positioned against the control's
  // viewport rect. Anchoring it to the field instead meant any ancestor with
  // overflow:hidden - the Card atom, a scrolling modal - clipped it.
  const position = useCallback(() => {
    const rect = controlRef.current?.getBoundingClientRect();
    if (!rect) return;

    const below = window.innerHeight - rect.bottom;
    const above = rect.top;
    const dropUp = below < MENU_MAX_HEIGHT && above > below;
    const room = (dropUp ? above : below) - MENU_GAP - VIEWPORT_MARGIN;

    setMenuStyle({
      position: "fixed",
      left: rect.left,
      width: rect.width,
      ...(dropUp
        ? { bottom: window.innerHeight - rect.top + MENU_GAP }
        : { top: rect.bottom + MENU_GAP }),
      // Never taller than the room it has, so the last option stays reachable
      // even when the control sits close to an edge.
      maxHeight: Math.max(96, Math.min(MENU_MAX_HEIGHT, room)),
    });
  }, []);

  useLayoutEffect(() => {
    if (open) position();
  }, [open, position]);

  // Fixed coordinates go stale the moment anything scrolls, and the capture
  // phase is what catches scrolling of an inner container rather than the page.
  useEffect(() => {
    if (!open) return;
    const reposition = () => position();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open, position]);

  const choose = (option: Option) => {
    onChange(option.value);
    setOpen(false);
  };

  return (
    <div
      ref={rootRef}
      className={[
        "fld",
        // Legacy hooks: AssessmentGrid and AIGeneratedDietPlan size this
        // component through .select-container / .select-field.
        "select-container",
        error ? "fld-error" : "",
        disabled ? "fld-disabled" : "",
        wrapperClassName,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {label && (
        <label className="fld-label" htmlFor={fieldId}>
          {label}
          {required && <span className="fld-required">*</span>}
        </label>
      )}

      {/* Anchors the floating list to the control rather than to the whole
          field, which also carries the label and the helper line. */}
      <div className="fld-select-wrap">
        <div
          ref={controlRef}
          id={fieldId}
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-invalid={error || undefined}
          aria-describedby={helperText ? helperId : undefined}
          tabIndex={disabled ? -1 : 0}
          className={`fld-box fld-select select-field ${open ? "open" : ""}`}
          onClick={() => !disabled && setOpen((wasOpen) => !wasOpen)}
          onKeyDown={(event) => {
            if (disabled) return;
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setOpen((wasOpen) => !wasOpen);
            }
          }}
        >
          <span
            className={`fld-select-value ${selected ? "" : "placeholder"}`.trim()}
          >
            {selected ? selected.label : placeholder}
          </span>
          <span className={`fld-select-caret ${open ? "open" : ""}`.trim()}>
            <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M4 6l4 4 4-4"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </div>

        {open &&
          !disabled &&
          createPortal(
            <div
              ref={menuRef}
              className="fld-options"
              style={menuStyle}
              role="listbox"
              aria-labelledby={fieldId}
            >
              {items.length === 0 && (
                <div className="fld-options-empty">No options available</div>
              )}
              {items.map((option) => {
                const isSelected = String(option.value) === String(value);
                return (
                  <div
                    key={String(option.value)}
                    role="option"
                    aria-selected={isSelected}
                    className={`fld-option ${isSelected ? "selected" : ""}`.trim()}
                    onClick={() => choose(option)}
                  >
                    <span>{option.label}</span>
                    {isSelected && (
                      <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <path
                          d="M3 8.5l3.2 3.2L13 5"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                );
              })}
            </div>,
            document.body,
          )}
      </div>

      {helperText && (
        <div className="fld-helper" id={helperId}>
          {helperText}
        </div>
      )}
    </div>
  );
};

export default Select;
